import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X, Phone } from "lucide-react";
import { NAV_LINKS, LOGO_URL, BUSINESS } from "../lib/data";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background/90 backdrop-blur-xl border-b border-border shadow-[0_4px_24px_-12px_rgba(15,44,68,0.15)]"
          : "bg-background/60 backdrop-blur-md"
      }`}
    >
      <div className="w-full max-w-[1520px] mx-auto px-4 md:px-6 lg:px-10 flex flex-nowrap items-center justify-between gap-4 h-20 lg:h-24">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 shrink-0 group">
          <div className="relative">
            <img src={LOGO_URL} alt="Ateng K Sarap" className="w-11 h-11 lg:w-12 lg:h-12 object-contain relative z-10" />
            <span className="absolute -inset-1 rounded-full bg-accent/40 blur-md opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <div className="hidden sm:block leading-tight whitespace-nowrap">
            <div className="font-display text-lg lg:text-xl font-semibold text-foreground tracking-tight">
              Ateng K Sarap
            </div>
            <div className="hidden xl:block font-script text-base text-secondary -mt-1">
              Pinoy Fusion Catering
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7 flex-nowrap">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative text-[13px] xl:text-[14px] font-medium tracking-wide whitespace-nowrap transition-colors duration-200 py-1 ${
                  isActive ? "text-secondary" : "text-foreground/80 hover:text-primary"
                }`
              }
              end={l.to === "/"}
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 right-0 h-1 wavy-underline" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 lg:gap-3 shrink-0">
          <a
            href={BUSINESS.phoneLink}
            data-testid="nav-phone-cta"
            className="hidden md:inline-flex lg:hidden xl:inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap text-primary hover:text-secondary transition-colors"
          >
            <Phone className="w-4 h-4" /> {BUSINESS.phone}
          </a>
          <Link to="/contact" data-testid="nav-book-now-btn" className="hidden lg:inline-flex btn-secondary !py-2.5 !px-5 text-[11px]">
            Book Us Now
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="lg:hidden p-2 text-foreground"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu-panel" className="lg:hidden bg-background border-t border-border">
          <nav className="container-eds py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 border-b border-border text-base font-medium ${isActive ? "text-secondary" : "text-foreground"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="mobile-book-now-btn" className="btn-secondary mt-5 w-full">
              Book Us Now
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
